// src/hooks/useTemplate.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { loadFromLocalStorage, saveToLocalStorage } from '../services/dataService';

const useTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Ref pour éviter les re-rendus inutiles
  const currentTemplateRef = useRef(null);
  const selectedElementRef = useRef(null);

  // Synchroniser les refs
  useEffect(() => {
    currentTemplateRef.current = currentTemplate;
    selectedElementRef.current = selectedElement;
  }, [currentTemplate, selectedElement]);

  // Charger les templates au démarrage
  useEffect(() => {
    loadTemplates();
  }, []);

  // Charger les templates depuis localStorage ou fichier par défaut
  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const loadedTemplates = await loadFromLocalStorage('templates');
      setTemplates(loadedTemplates);
      
      // Sélectionner le premier template par défaut
      if (loadedTemplates.length > 0 && !currentTemplateRef.current) {
        setCurrentTemplate(loadedTemplates[0]);
      }
    } catch (err) {
      setError('Erreur lors du chargement des templates');
      console.error('Erreur loadTemplates:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sauvegarder les templates de manière optimisée
  const saveTemplates = useCallback(async (updatedTemplates) => {
    try {
      await saveToLocalStorage('templates', updatedTemplates);
      setTemplates(updatedTemplates);
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error('Erreur saveTemplates:', err);
    }
  }, []);

  // Créer un nouveau template
  const createTemplate = useCallback(async (templateData) => {
    const newTemplate = {
      id: `template-${Date.now()}`,
      name: templateData.name || 'Nouveau Template',
      category: templateData.category || 'custom',
      elements: templateData.elements || [],
      background: templateData.background || {
        type: 'gradient',
        value: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)'
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setTemplates(prev => {
      const updatedTemplates = [...prev, newTemplate];
      saveTemplates(updatedTemplates);
      return updatedTemplates;
    });
    
    setCurrentTemplate(newTemplate);
    return newTemplate;
  }, [saveTemplates]);

  // Mettre à jour le template - VERSION OPTIMISÉE
  const updateTemplate = useCallback((updates) => {
    if (!currentTemplateRef.current) return;

    let updatedTemplate;
    
    if (typeof updates === 'function') {
      updatedTemplate = updates(currentTemplateRef.current);
    } else {
      updatedTemplate = {
        ...currentTemplateRef.current,
        ...updates,
        updatedAt: new Date().toISOString()
      };
    }

    // Optimisation : éviter les mises à jour inutiles
    if (JSON.stringify(updatedTemplate) === JSON.stringify(currentTemplateRef.current)) {
      return updatedTemplate;
    }

    // Mettre à jour de manière synchrone pour éviter les problèmes de focus
    setCurrentTemplate(updatedTemplate);
    
    // Mettre à jour dans la liste des templates
    setTemplates(prev => {
      const updatedTemplates = prev.map(template =>
        template.id === updatedTemplate.id ? updatedTemplate : template
      );
      
      // Sauvegarder de manière asynchrone sans bloquer l'UI
      setTimeout(() => saveTemplates(updatedTemplates), 0);
      
      return updatedTemplates;
    });

    // Mettre à jour l'élément sélectionné si nécessaire
    if (selectedElementRef.current && updatedTemplate.elements) {
      const updatedElement = updatedTemplate.elements.find(
        el => el.id === selectedElementRef.current.id
      );
      if (updatedElement && JSON.stringify(updatedElement) !== JSON.stringify(selectedElementRef.current)) {
        setSelectedElement(updatedElement);
      }
    }
    
    return updatedTemplate;
  }, [saveTemplates]);

  // Mettre à jour un élément spécifique - VERSION OPTIMISÉE
  const updateElement = useCallback((elementId, updates) => {
    if (!currentTemplateRef.current) return;

    const updatedElements = currentTemplateRef.current.elements.map(element =>
      element.id === elementId ? { ...element, ...updates } : element
    );

    const updatedTemplate = {
      ...currentTemplateRef.current,
      elements: updatedElements,
      updatedAt: new Date().toISOString()
    };

    updateTemplate(updatedTemplate);
  }, [updateTemplate]);

  // Sélectionner un élément - VERSION OPTIMISÉE
  const selectElement = useCallback((element) => {
    // Éviter les re-rendus inutiles
    if (element?.id === selectedElementRef.current?.id) return;
    
    if (element && currentTemplateRef.current) {
      // S'assurer que l'élément existe toujours dans le template
      const currentElement = currentTemplateRef.current.elements?.find(el => el.id === element.id);
      setSelectedElement(currentElement || null);
    } else {
      setSelectedElement(null);
    }
  }, []);

  // Ajouter un nouvel élément
  const addElement = useCallback(async (elementData) => {
    if (!currentTemplateRef.current) return;

    const newElement = {
      id: `${elementData.type}-${Date.now()}`,
      type: elementData.type,
      ...elementData
    };

    const updatedElements = [...(currentTemplateRef.current.elements || []), newElement];
    const updatedTemplate = {
      ...currentTemplateRef.current,
      elements: updatedElements,
      updatedAt: new Date().toISOString()
    };
    
    updateTemplate(updatedTemplate);
    setSelectedElement(newElement);
    
    return newElement;
  }, [updateTemplate]);

  // Supprimer un élément
  const deleteElement = useCallback((elementId) => {
    if (!currentTemplateRef.current) return;

    const updatedElements = currentTemplateRef.current.elements.filter(
      element => element.id !== elementId
    );

    const updatedTemplate = {
      ...currentTemplateRef.current,
      elements: updatedElements,
      updatedAt: new Date().toISOString()
    };

    updateTemplate(updatedTemplate);
    
    // Désélectionner l'élément s'il était sélectionné
    if (selectedElementRef.current?.id === elementId) {
      setSelectedElement(null);
    }
  }, [updateTemplate]);

  // Dupliquer un élément
  const duplicateElement = useCallback(async (element) => {
    if (!currentTemplateRef.current) return;

    const duplicatedElement = {
      ...element,
      id: `${element.type}-${Date.now()}`,
      x: element.x + 20,
      y: element.y + 20
    };

    const updatedElements = [...currentTemplateRef.current.elements, duplicatedElement];
    const updatedTemplate = {
      ...currentTemplateRef.current,
      elements: updatedElements,
      updatedAt: new Date().toISOString()
    };

    updateTemplate(updatedTemplate);
    setSelectedElement(duplicatedElement);
    
    return duplicatedElement;
  }, [updateTemplate]);

  // Supprimer un template
  const deleteTemplate = useCallback(async (templateId) => {
    const updatedTemplates = templates.filter(template => template.id !== templateId);
    await saveTemplates(updatedTemplates);
    
    // Si le template supprimé était le template actuel, sélectionner le premier disponible
    if (currentTemplateRef.current?.id === templateId) {
      setCurrentTemplate(updatedTemplates.length > 0 ? updatedTemplates[0] : null);
      setSelectedElement(null);
    }
  }, [templates, saveTemplates]);

  // Dupliquer un template
  const duplicateTemplate = useCallback(async (template) => {
    const duplicatedTemplate = {
      ...template,
      id: `template-${Date.now()}`,
      name: `${template.name} (Copie)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const updatedTemplates = [...templates, duplicatedTemplate];
    await saveTemplates(updatedTemplates);
    
    setCurrentTemplate(duplicatedTemplate);
    return duplicatedTemplate;
  }, [templates, saveTemplates]);

  // Réinitialiser l'état
  const resetState = useCallback(() => {
    setCurrentTemplate(null);
    setSelectedElement(null);
    setError(null);
  }, []);

  // Obtenir les templates par catégorie
  const getTemplatesByCategory = useCallback((category) => {
    return templates.filter(template => template.category === category);
  }, [templates]);

  // Rechercher des templates
  const searchTemplates = useCallback((query) => {
    const lowercaseQuery = query.toLowerCase();
    return templates.filter(template =>
      template.name.toLowerCase().includes(lowercaseQuery) ||
      template.category.toLowerCase().includes(lowercaseQuery)
    );
  }, [templates]);

  // Obtenir les statistiques
  const getStats = useCallback(() => {
    const totalTemplates = templates.length;
    const categories = [...new Set(templates.map(t => t.category))];
    const mostRecentTemplate = templates.reduce((latest, template) => {
      return new Date(template.updatedAt) > new Date(latest.updatedAt || 0) 
        ? template 
        : latest;
    }, {});

    return {
      totalTemplates,
      categories: categories.length,
      mostRecentTemplate: mostRecentTemplate.name || 'Aucun',
      elementsInCurrentTemplate: currentTemplateRef.current?.elements?.length || 0
    };
  }, [templates]);

  return {
    // État
    templates,
    currentTemplate,
    selectedElement,
    isLoading,
    error,

    // Actions sur les templates
    loadTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    setCurrentTemplate,

    // Actions sur les éléments
    updateElement,
    addElement,
    deleteElement,
    duplicateElement,
    selectElement,

    // Utilitaires
    resetState,
    getTemplatesByCategory,
    searchTemplates,
    getStats
  };
};

export default useTemplates;